import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Close from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import {
  addTransactionTypeApi,
  updateTransactionTypeApi,
} from "../../../services/transaction-type.service";
import {
  CAPITALIZE,
  PAYMENT_CHARGES_TYPE,
  PAYMENT_CLEAR_MODE,
  PAYMENT_METHOD,
  TRANSACTION_MODE,
} from "../../../utils/constants";

export default function TransactionTypeModal({
  open,
  onClose,
  refetch,
  selectedRow = null,
}) {
  const initialState = {
    name: "Card Payment",
    paymentMethod: "Debit/Credit Card",
    paymentClearMode: "Auto",
    transactionModes: ["Payment", "Refund", "Pending Payment"],
    bankDetails: [
      {
        bankName: "ABC Bank",
        ifscCode: "ABC0001234",
        accountNumber: "123456789",
        accountHolderName: "John Doe",
        default: true,
      },
    ],
    active: true,
    paymentChargesType: "percent",
    paymentCharges: 2.5,
  };

  const [formData, setFormData] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((preValue) => ({
      ...preValue,
      [name]:
        type === "checkbox"
          ? checked
          : name === "paymentCharges"
          ? Number(value)
          : value,
    }));
  };

  const handleBankDetailsChange = (e, index, type) => {
    const { name, value, checked } = e.target;

    setFormData((prev) => {
      const updatedBankDetails = prev.bankDetails.map((bank, i) => {
        if (i === index) {
          if (name === "default") {
            if (prev.bankDetails.length === 1) {
              // Ensure default is always true for single bank
              return { ...bank, default: true };
            }
            return { ...bank, default: checked };
          }
          return { ...bank, [name]: type === "checkbox" ? checked : value };
        }
        return name === "default" ? { ...bank, default: false } : bank;
      });

      return { ...prev, bankDetails: updatedBankDetails };
    });
  };

  const addBankDetail = () => {
    setFormData((prev) => ({
      ...prev,
      bankDetails: [
        ...prev.bankDetails,
        {
          bankName: "",
          ifscCode: "",
          accountNumber: "",
          accountHolderName: "",
          default: false,
        },
      ],
    }));
  };

  const removeBankDetail = (index) => {
    setFormData((prev) => ({
      ...prev,
      bankDetails: prev.bankDetails.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let response;

    // const { error } = UnitSchema.validate(formData);

    // if (error) {
    //   error.details.forEach((err) => toast.error(err.message));
    //   return;
    // }

    setIsLoading(true);
    if (selectedRow?._id) {
      response = await updateTransactionTypeApi(formData);
    } else {
      response = await addTransactionTypeApi(formData);
    }
    setIsLoading(false);

    if (response?.isSuccess) {
      toast.success(
        response?.message ||
          `Transaction type ${
            selectedRow?._id ? "updated" : "added"
          } successfully`
      );
      setFormData(initialState);
      onClose();
      refetch();
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  // Sync selectedRow data when modal opens
  useEffect(() => {
    if (selectedRow?._id) {
      setFormData({
        ...initialState,
        _id: selectedRow._id,
        name: selectedRow?.name || "",
        paymentMethod: selectedRow?.paymentMethod || "",
        paymentClearMode: selectedRow?.paymentClearMode || "",
        transactionModes: selectedRow?.transactionModes || "",
        bankDetails: selectedRow?.bankDetails || [],
        active: selectedRow?.active ?? true,
        paymentChargesType: selectedRow?.paymentChargesType || "",
        paymentCharges: selectedRow?.paymentCharges ?? 0,
      });
    } else {
      setFormData(initialState);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            maxWidth: "unset",
            width: { xs: "95vw", lg: "1100px" },
            bgcolor: "#E5F4FF",
            borderRadius: "20px",
          },
        },
      }}
    >
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: "20px",
          }}
        >
          <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
            Add Transaction Type
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Name"
                name="name"
                required
                fullWidth
                value={formData?.name || ""}
                onChange={handleChange}
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={PAYMENT_METHOD || []}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Payment Method"
                    required
                    fullWidth
                  />
                )}
                value={formData?.paymentMethod || ""}
                onChange={(_, newValue) =>
                  handleChange({
                    target: { name: "paymentMethod", value: newValue },
                  })
                }
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={PAYMENT_CLEAR_MODE || []}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Payment Clear Mode"
                    required
                    fullWidth
                  />
                )}
                value={formData?.paymentClearMode || ""}
                onChange={(_, newValue) =>
                  handleChange({
                    target: { name: "paymentClearMode", value: newValue },
                  })
                }
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Autocomplete
                multiple
                limitTags={2}
                disableCloseOnSelect
                options={TRANSACTION_MODE || []}
                renderInput={(params) => (
                  <TextField {...params} label="Transaction Modes" fullWidth />
                )}
                value={formData?.transactionModes || []}
                onChange={(_, newValues) =>
                  handleChange({
                    target: { name: "transactionModes", value: newValues },
                  })
                }
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="active"
                    checked={formData?.active}
                    onChange={handleChange}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={PAYMENT_CHARGES_TYPE || []}
                getOptionLabel={(option) => CAPITALIZE(option) || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Payment Charges Type"
                    required
                    fullWidth
                  />
                )}
                value={formData?.paymentChargesType || ""}
                onChange={(_, newValue) =>
                  handleChange({
                    target: { name: "paymentChargesType", value: newValue },
                  })
                }
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                type="number"
                label="Payment Charges"
                name="paymentCharges"
                fullWidth
                value={formData?.paymentCharges || ""}
                onChange={handleChange}
                sx={{ bgcolor: "white" }}
              />
            </Grid>
          </Grid>

          {/* Bank Details section */}

          <Box
            sx={{
              my: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="h6">Bank Details</Typography>

            <Button
              variant="contained"
              sx={{ textTransform: "none" }}
              onClick={addBankDetail}
            >
              Add Bank
            </Button>
          </Box>

          {formData?.bankDetails?.map((bank, index) => (
            <Grid container spacing={2} sx={{ mb: "20px" }}>
              {index > 0 && (
                <Grid item xs={12} sx={{ textAlign: "right" }}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => removeBankDetail(index)}
                  >
                    Remove
                  </Button>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Bank Name"
                  name="bankName"
                  required
                  fullWidth
                  value={bank?.bankName}
                  onChange={(e) => handleBankDetailsChange(e, index)}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="IFSC Code"
                  name="ifscCode"
                  required
                  fullWidth
                  value={bank?.ifscCode}
                  onChange={(e) => handleBankDetailsChange(e, index)}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Account Number"
                  name="accountNumber"
                  required
                  fullWidth
                  value={bank?.accountNumber}
                  onChange={(e) => handleBankDetailsChange(e, index)}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Account Holder Name"
                  name="accountHolderName"
                  required
                  fullWidth
                  value={bank?.accountHolderName}
                  onChange={(e) => handleBankDetailsChange(e, index)}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={bank?.default}
                      onChange={(e) =>
                        handleBankDetailsChange(e, index, "checkbox")
                      }
                      name="default"
                      disabled={formData?.bankDetails?.length === 1} // Disable if only one bank
                    />
                  }
                  label="Default Bank"
                />
              </Grid>
            </Grid>
          ))}

          <Box sx={{ display: "flex", justifyContent: "center", mt: "20px" }}>
            <Button
              variant="contained"
              type="submit"
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : selectedRow?._id ? (
                "Save Changes"
              ) : (
                `Submit`
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
